// Chamadas da API usadas pelo app, com os tipos gerados de schemas/v1 (src/contratos, no build).
import type {
  AnalisesDaSessao,
  CatalogoModelos,
  EntradaRegistroSelecao,
  PedidoModelos,
  Recibo,
  VistaCard,
} from "../contratos";

import { api, enviarJson } from "./cliente";

export type Estado =
  | "recebida"
  | "preparada"
  | "consultando"
  | "julgando"
  | "redigindo"
  | "conferindo"
  | "concluida"
  | "falhou";

export interface RespostaVista {
  estado: Estado;
  atualizar_em: number;
  vista: VistaCard | null;
}

export interface ReciboPublico {
  url_card: string;
  hash_dossie: string;
  analise: {
    id: string;
    versao: number;
    versao_anterior: number | null;
    criada_em: string;
    evidencias: string[];
    consulta: string | null;
    consultado_em: string | null;
  };
  recibo: Recibo | null;
}

export interface Metodo {
  metodo_versao: string;
  alfa: number;
  codigo_url: string;
  commit: string | null;
  motivos: { codigo: string; texto: string }[];
  armadilhas: { codigo: string; titulo: string }[];
}

export const entrar = (codigo: string) => api<undefined>("/api/v1/sessao", enviarJson({ codigo }));
export const verSessao = () => api<undefined>("/api/v1/sessao");

/** Modelos escolhíveis, perfis e etapas (ADR 0015); sem endpoint nem chave. */
export const verModelos = () => api<CatalogoModelos>("/api/v1/modelos");

/** As análises recentes desta sessão; só a alegação em forma neutra, nunca o texto enviado. */
export const verHistorico = () => api<AnalisesDaSessao>("/api/v1/analises");

export interface PedidoAnalise {
  texto: string;
  link: string;
  /** Perfil, modelo e esforço ou etapas; vazio usa o padrão do operador (ADR 0015). */
  modelos: PedidoModelos;
  arquivo: File | null;
}

/** Com arquivo, vai em multipart; a imagem é lida no servidor e nunca guardada (ADR 0014). */
export function analisar({ texto, link, modelos, arquivo }: PedidoAnalise) {
  type Criado = { card_id: string; url_privada: string; estado: string };
  const papeis = modelos.papeis && Object.keys(modelos.papeis).length ? modelos.papeis : null;
  if (!arquivo) {
    return api<Criado>(
      "/api/v1/analises",
      // Só o link também vale: ele vira o texto, e o servidor busca a página de origem.
      enviarJson({
        texto: texto || link,
        cortar: true,
        ...(link ? { link } : {}),
        ...(modelos.perfil ? { perfil: modelos.perfil } : {}),
        ...(modelos.modelo ? { modelo: modelos.modelo } : {}),
        ...(modelos.esforco ? { esforco: modelos.esforco } : {}),
        ...(papeis ? { papeis } : {}),
      }),
    );
  }
  const corpo = new FormData();
  corpo.set("texto", texto);
  corpo.set("cortar", "true");
  if (link) corpo.set("link", link);
  if (modelos.perfil) corpo.set("perfil", modelos.perfil);
  if (modelos.modelo) corpo.set("modelo", modelos.modelo);
  if (modelos.esforco) corpo.set("esforco", modelos.esforco);
  if (papeis) corpo.set("papeis", JSON.stringify(papeis));
  corpo.set("arquivo", arquivo);
  return api<Criado>("/api/v1/analises", { method: "POST", body: corpo });
}

export const verVista = (chave: string, versao: string | null) =>
  api<RespostaVista>(
    `/api/v1/cards/${encodeURIComponent(chave)}/vista${versao ? `?versao=${encodeURIComponent(versao)}` : ""}`,
  );

/** `url` vem da vista (`url_compartilhar`), montada pelo servidor. */
export const compartilhar = (url: string) => api<{ url_publica: string }>(url, { method: "POST" });

/** `url` vem da vista (`url_contestar`), montada pelo servidor. */
export const contestar = (url: string, argumento: string, linkEvidencia: string) =>
  api<{ contestacao_id: string }>(
    url,
    enviarJson({ argumento, ...(linkEvidencia ? { link_evidencia: linkEvidencia } : {}) }),
  );

export const verRecibo = (id: string) =>
  api<ReciboPublico>(`/api/v1/cards/${encodeURIComponent(id)}/recibo`);

export const verMetodo = () => api<Metodo>("/api/v1/metodo");

export const verSelecao = (parametros: URLSearchParams) =>
  api<EntradaRegistroSelecao[]>(`/api/v1/selecao?${parametros.toString()}`);

export const verVistaDaAnalise = (id: string, versao: string | null) =>
  api<RespostaVista>(
    `/api/v1/analises/${encodeURIComponent(id)}/vista${versao ? `?versao=${encodeURIComponent(versao)}` : ""}`,
  );

export const textoDaAnalise = (id: string) => `/api/v1/analises/${encodeURIComponent(id)}/texto`;

export const textoDoCard = (chave: string) => `/api/v1/cards/${encodeURIComponent(chave)}/texto`;

export const imagemDoCard = (chave: string) =>
  `/api/v1/cards/${encodeURIComponent(chave)}/imagem.svg`;
